function zip<U, V>(a: U[], b: V[]): [U, V][] {
    let ret: [U, V][] = [];
    for (let i = 0; i < a.length; i++) {
        if (i >= b.length) {
            break;
        }
        ret.push([a[i], b[i]]);
    }
    return ret;
}
export class Vector {
    readonly els: number[];
    constructor(els: number[])
    constructor(..._els: number[]) 
    constructor(...args: any[]){
        if (typeof args[0] === 'number') {
            this.els = args;
        }
        else {
            this.els = args[0];
        }
    }
    equal(b: this) {
        return zip(this.els, b.els).every(z => z[0] === z[1]);
    }

    norm() {
        return this.lnorm(2);
    }
    normalize() {
        return this.scalar(1/this.norm());
    }
    lnorm(n: number) {
        let val = this.els.reduce((p, c) => p + Math.pow(c, n), 0);
        return Math.pow(val, 1/n);
    }
    dot(b: Vector) {
        if (b.els.length !== this.els.length) {
            return undefined;
        }
        return zip(this.els, b.els).map(p => p[0] * p[1]).reduce((prev, cur) => prev + cur, 0);
    }
    angle(b: Vector) {
        if (this.els.length !== b.els.length) {
            return undefined;
        }
        if (this.els.length == 2) {
            return Math.atan2(b.els[1], b.els[0]) - Math.atan2(this.els[1], this.els[0]);
        }
        else {
            Math.acos(this.dot(b) / (this.norm() * b.norm()));
        }
    }
    plus(b: Vector): this {
        if (this.els.length !== b.els.length) {
            return undefined;
        }
        let z = zip(this.els, b.els);
        let map = z.map(p => p[0] + p[1]);
        return new (<any>this).constructor(map);     
    }
    minus(b: Vector): this {
        if (this.els.length !== b.els.length) {
            return undefined;
        }
        let z = zip(this.els, b.els);
        let map = z.map(p => p[0] - p[1]);
        return new (<any>this).constructor(map);
    }

    scalar(s: number): this {
        return new (<any> this).constructor(this.els.map(v => s * v));
    }

    /* The vector v in the same direction as this, but with ||v|| = s */
    scaleTo(s: number): this {
        let l = this.norm();
        return this.scalar(s/l);
    }
}
export class AxisAlignedBox2 {
    readonly topLeft: Vec2;
    readonly bottomRight: Vec2;
    constructor(topLeft: Vec2, bottomRight: Vec2) {
        this.topLeft = topLeft;
        this.bottomRight = bottomRight;
    }
}
export class Vec2 extends Vector {
    x: number;
    y: number;
    constructor(val: { x: number; y: number })
    constructor(x: number, y: number) 
    constructor (...args: any[]) {
        let x: number;
        let y: number;
        if (args[0] instanceof Array) {
            x = args[0][0];
            y = args[0][1];
        }
        else if (typeof args[0] === 'number') {
            x = args[0];
            y = args[1];
        }
        else {
            x = args[0].x;
            y = args[0].y;
        }
        super(x, y);
        this.x = x;
        this.y = y;
    }
    angleXAxisDeg() {
        return this.angleXAxis() * 180 / Math.PI;
    }
    angleXAxis() {
        return this.angle(new Vec2(1, 0));
    }
    d(): string {
        return (this.x + "," + this.y + " ");
    }
    /* adopted from https://www.scratchapixel.com/lessons/3d-basic-rendering/minimal-ray-tracer-rendering-simple-shapes/ray-box-intersection */
    /* where does a ray originating at origin intersect an AABB */
    boxIntersectionPoint(box: AxisAlignedBox2, origin = new Vec2(0, 0)): Vec2 | undefined {
        /* this = d*t + origin; */
        let tl = box.topLeft.minus(origin);
        let br = box.bottomRight.minus(origin);

        let d = this.minus(origin).normalize();

        let t0x = (tl.x) / d.x;
        let t1x = (br.x) / d.x;
        let t0y = (tl.y) / d.y;
        let t1y = (br.y) / d.y;

        let txmin = Math.min(t0x, t1x);
        let txmax = Math.max(t0x, t1x);
        let tymin = Math.min(t0y, t1y);
        let tymax = Math.max(t0y, t1y);

        let miss =  (tymax < txmin || txmax < tymin);
        if (miss) return undefined;

        /* first t that lands on the box */
        let t = txmin > tymin ? txmin : tymin;
        return new Vec2(d.scalar(t)).plus(origin);
    }
}


// WEBPACK FOOTER //
// ./src/code/Math/Algebra.ts